import React, { useMemo } from "react";
import { Formik } from "formik";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import * as Actions from "actions";
import { EditorForm } from "../EditorForm";
import { updateSectionRules, getInitialRules } from "../../Functions";
import { DEFAULT_RULE } from "constants/TemplateEditor";
import _ from "lodash";
import { RuleEditorValidationSchema } from "components/RuleEditor/Validations";
import { $MessageBox } from "us.common/components";

interface IRuleEditor {
  onCancel: () => void;
}

const SectionRule: React.FC<IRuleEditor & PropsFromRedux> = (props) => {
  const {
    template,
    section,
    tokens,
    onCancel,
    reformRules,
    updateTemplate,
    selectSection,
  } = props;

  const templateContent = template.data?.templateContent ?? {};

  const { defaultRule, initialRules } = useMemo(() => {
    return getInitialRules(templateContent, section);
  }, [templateContent, section]);

  const handleSubmit = (values: any, helpers: any) => {
    try {
      const { updatedTemplate, rules } = updateSectionRules(
        values,
        template,
        tokens,
        defaultRule,
        section
      );

      updatedTemplate && updateTemplate && updateTemplate(updatedTemplate);

      rules && reformRules && reformRules(rules);
      setDefaultSelectedRule(rules);

      helpers.resetForm();
      onCancel();

      $MessageBox(
        "success",
        `US.USC.TEMPLATEEDITOR:MESSAGES.SECTION_RULE_UPDATE_SUCCESS`,
        "",
        ""
      );
    } catch (error) {
      console.error("Section rule update fail", error);
      $MessageBox(
        "error",
        `US.USC.TEMPLATEEDITOR:MESSAGES.SECTION_RULE_UPDATE_FAIL`,
        "",
        ""
      );
    }
  };

  /**
   * @description - Handle rule selection
   */
  const setDefaultSelectedRule = (rules: Array<any>) => {
    try {
      rules &&
        rules.map(({ rule, isDeleted }: any, _index: number) => {
          if (rule == DEFAULT_RULE) {
            const selectedRuleHtml = document.querySelector(
              `div[sectionname='${section.name}'][rule='${rule}']`
            );
            selectedRuleHtml?.removeAttribute("hidden");
            selectSection &&
              selectSection({
                ...section,
                rules,
                selectedRule: DEFAULT_RULE,
                id: selectedRuleHtml?.getAttribute("id"),
              });
          } else {
            const otherRuleHtml = document.querySelector(
              `div[sectionname='${section.name}'][rule='${rule}']`
            );
            otherRuleHtml?.setAttribute("hidden", "true");
            isDeleted && otherRuleHtml?.setAttribute("deleted", "true");
            isDeleted && otherRuleHtml?.removeAttribute("rule");
          }
        });
    } catch (e) {
      console.error("Set default rule error ", e);
    }
  };

  return (
    <Formik
      initialValues={{
        rules: [...initialRules],
      }}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnMount
      validateOnChange
      validateOnBlur
      validationSchema={RuleEditorValidationSchema(tokens)}
    >
      {({ handleSubmit }: any) => (
        <EditorForm
          handleSubmit={handleSubmit}
          onCancel={onCancel}
          defaultRule={defaultRule}
        />
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templateEditor, templatesHistory } = state;
  const { section, parameters } = templateEditor;
  const { template } = templatesHistory;
  return {
    section,
    template,
    tokens: parameters.standard.dataTokens,
  };
};

const { section } = Actions.templateEditor;
const { template } = Actions.templatesHistory;

const mapDispatchToProps = {
  reformRules: section.reformRules,
  updateTemplate: template.update,
  selectSection: section.selectSection,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(SectionRule);
